import { FC, PropsWithChildren, useMemo, useRef, useState } from 'react';

import { AsyncDataContext, AsyncDataContextProps } from '@hh.ru/magritte-ui-date-picker/AsyncDataContext';
import { DateType } from '@hh.ru/magritte-ui-date-picker/types';

interface AsyncDataStoreProps {
    disabledDatesFunc?: (startDate: DateType, endDate: DateType) => Promise<DateType[]>;
    parallelLoadersLimit?: number | null;
}

const makeKey = (startDate: DateType, endDate: DateType) => {
    return `${new Date(startDate).getTime()}-${new Date(endDate).getTime()}`;
};

/**
 * Хранит данные, загруженные всеми вложенными AsyncDataLoader-ами, и предоставляет методы для загрузки данных
 */
export const AsyncDataStore: FC<PropsWithChildren<AsyncDataStoreProps>> = ({
    children,
    disabledDatesFunc,
    parallelLoadersLimit = null,
}) => {
    const asyncDataState = useRef<Record<string, { promise: Promise<void>; state: 'loading' | 'done' }>>({});
    const [disabledDates, setDisabledDates] = useState<DateType[]>([]);

    const value = useMemo<AsyncDataContextProps | null>(() => {
        if (!disabledDatesFunc) {
            return null;
        }

        return {
            disabledDates,
            hasAsyncFunc: true,
            hasData: (startDate: DateType, endDate: DateType) => makeKey(startDate, endDate) in asyncDataState.current,
            startAsyncLoading: () => void 0,
            awaitAsyncLoading: (startDate: DateType, endDate: DateType) => {
                const key = makeKey(startDate, endDate);
                if (!(key in asyncDataState.current)) {
                    asyncDataState.current[key] = {
                        promise: new Promise<void>(async (resolve) => {
                            try {
                                const _disabledDates = await disabledDatesFunc(startDate, endDate);
                                setDisabledDates((disabledDates) => [...disabledDates, ..._disabledDates]);
                            } finally {
                                asyncDataState.current[key].state = 'done';
                                resolve();
                            }
                        }),
                        state: 'loading',
                    };
                }
                return asyncDataState.current[key].promise;
            },
            isLoading: () => Object.values(asyncDataState.current).some(({ state }) => state === 'loading'),
            isLimitExceeded: () =>
                parallelLoadersLimit !== null &&
                Object.values(asyncDataState.current).filter(({ state }) => state === 'loading').length >=
                    parallelLoadersLimit,
        };
    }, [disabledDates, disabledDatesFunc, setDisabledDates, parallelLoadersLimit]);

    return <AsyncDataContext.Provider value={value}>{children}</AsyncDataContext.Provider>;
};
